import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Portal,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';

type ChatMessage = {
  sender: 'user' | 'bot';
  text: string;
};

type ConversationEntry = {
  role: 'user' | 'assistant';
  content: string;
};

type BotConfigResponse = {
  personality?: {
    greeting?: string;
  };
  quickActions?: string[];
};

const API_CONFIG = {
  BASE_URL: 'https://chat.themetasum.com',
  ENDPOINTS: {
    CHAT: '/api/chat',
    CONFIG: '/api/bot-config',
  },
};

const BOT_CONFIG = {
  NAME: 'Nova',
  SUBTITLE: 'POS Support Assistant',
};

const DEFAULT_QUICK_ACTIONS = [
  'How do I apply a discount?',
  'How do I add a new employee?',
  'How do I manage tips?',
];

interface NovaChatPanelProps {
  open: boolean;
  onClose: () => void;
}

export default function NovaChatPanel({ open, onClose }: NovaChatPanelProps) {
  const theme = useTheme();
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [quickActions, setQuickActions] = useState<string[]>(DEFAULT_QUICK_ACTIONS);
  const [history, setHistory] = useState<ConversationEntry[]>([]);
  const hasInitializedRef = useRef(false);
  const messagesRef = useRef<HTMLDivElement | null>(null);

  const addMessage = useCallback((text: string, sender: ChatMessage['sender']) => {
    setMessages((prev) => [...prev, { text, sender }]);
  }, []);

  const loadConfig = useCallback(async () => {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CONFIG}`);
      const config = (await response.json()) as BotConfigResponse;
      const greeting = config?.personality?.greeting
        || `Hello! I am ${BOT_CONFIG.NAME}, your POS support assistant. How may I assist you today?`;
      const actions = config?.quickActions?.length ? config.quickActions : DEFAULT_QUICK_ACTIONS;
      setMessages([{ sender: 'bot', text: greeting }]);
      setQuickActions(actions);
      setHistory([]);
    } catch (error) {
      console.error('Failed to load Nova chat config:', error);
      setMessages([
        {
          sender: 'bot',
          text: `Hello! I am ${BOT_CONFIG.NAME}, your POS support assistant. How may I assist you today?`,
        },
      ]);
      setQuickActions(DEFAULT_QUICK_ACTIONS);
      setHistory([]);
    }
  }, []);

  useEffect(() => {
    if (open && !hasInitializedRef.current) {
      hasInitializedRef.current = true;
      void loadConfig();
    }
  }, [open, loadConfig]);

  useEffect(() => {
    if (messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const handleSend = useCallback(async () => {
    const message = input.trim();
    if (!message || loading) return;

    addMessage(message, 'user');
    setInput('');
    setLoading(true);

    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CHAT}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message,
          history,
        }),
      });

      const data = (await response.json()) as { reply?: string };
      const reply = data?.reply || 'Sorry, I encountered an error. Please try again.';
      addMessage(reply, 'bot');
      setHistory((prev) => [
        ...prev,
        { role: 'user', content: message },
        { role: 'assistant', content: reply },
      ]);
    } catch (error) {
      console.error('Nova chat error:', error);
      addMessage('Sorry, I encountered an error. Please try again.', 'bot');
    } finally {
      setLoading(false);
    }
  }, [addMessage, history, input, loading]);

  const handleQuickAction = useCallback(
    async (action: string) => {
      if (loading) return;

      addMessage(action, 'user');
      setLoading(true);
      setInput('');

      try {
        const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CHAT}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            message: action,
            history,
          }),
        });

        const data = (await response.json()) as { reply?: string };
        const reply = data?.reply || 'Sorry, I encountered an error. Please try again.';
        addMessage(reply, 'bot');
        setHistory((prev) => [
          ...prev,
          { role: 'user', content: action },
          { role: 'assistant', content: reply },
        ]);
      } catch (error) {
        console.error('Nova quick action error:', error);
        addMessage('Sorry, I encountered an error. Please try again.', 'bot');
      } finally {
        setLoading(false);
      }
    },
    [addMessage, history, loading]
  );

  const handleReset = useCallback(() => {
    void loadConfig();
  }, [loadConfig]);

  const panelSx = useMemo(
    () => ({
      position: 'fixed',
      bottom: 16,
      right: 16,
      width: { xs: 'calc(100vw - 24px)', sm: 380 },
      height: { xs: 'calc(100vh - 24px)', sm: 600 },
      bgcolor: 'background.paper',
      borderRadius: { xs: 1, sm: 2 },
      boxShadow: 8,
      display: open ? 'flex' : 'none',
      flexDirection: 'column',
      zIndex: theme.zIndex.modal + 2,
      overflow: 'hidden',
    }),
    [open, theme.zIndex.modal]
  );

  return (
    <Portal>
      <Box sx={panelSx}>
      <Box
        sx={{
          bgcolor: '#1a1a2e',
          color: 'common.white',
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <Typography variant="subtitle1" fontWeight={700} sx={{ color: 'common.white' }}>
            {BOT_CONFIG.NAME}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.8, color: 'common.white' }}>
            {BOT_CONFIG.SUBTITLE}
          </Typography>
        </Box>
        <IconButton size="small" sx={{ color: 'common.white' }} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box
        ref={messagesRef}
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: 2,
          bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : '#f5f5f5',
        }}
      >
        <Stack spacing={1.5}>
          {messages.map((message, idx) => (
            <Box
              key={`${message.sender}-${idx}`}
              sx={{
                alignSelf: message.sender === 'user' ? 'flex-end' : 'flex-start',
                maxWidth: '85%',
                px: 1.5,
                py: 1,
                borderRadius: 2,
                bgcolor: message.sender === 'user' ? '#1a1a2e' : 'background.paper',
                color: message.sender === 'user' ? 'common.white' : 'text.primary',
              }}
            >
              <Typography variant="body2">{message.text}</Typography>
            </Box>
          ))}
          {loading && (
            <Box
              sx={{
                alignSelf: 'flex-start',
                px: 1.5,
                py: 1,
                borderRadius: 2,
                bgcolor: 'background.paper',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <CircularProgress size={14} />
              <Typography variant="body2" color="text.secondary">
                Typing...
              </Typography>
            </Box>
          )}
        </Stack>
      </Box>

      <Box sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider', display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {quickActions.map((action) => (
          <Chip
            key={action}
            label={action}
            size="small"
            onClick={() => handleQuickAction(action)}
            sx={{ cursor: 'pointer' }}
          />
        ))}
      </Box>

      <Box sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider', display: 'flex', gap: 1 }}>
        <TextField
          id="nova-chat-input"
          fullWidth
          size="small"
          placeholder="Type your message..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void handleSend();
            }
          }}
        />
        <IconButton color="primary" onClick={() => void handleSend()} disabled={loading || !input.trim()}>
          <SendIcon />
        </IconButton>
      </Box>

      <Box sx={{ p: 1.5, pt: 0 }}>
        <Button fullWidth size="small" variant="outlined" onClick={handleReset}>
          Reset Conversation
        </Button>
      </Box>
      </Box>
    </Portal>
  );
}
